import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Lock,
  LogOut,
  Phone,
  Mail,
  MapPin,
  ClipboardList,
  CheckCircle2,
  Circle,
  Trash2,
  Truck,
  RefreshCw,
} from "lucide-react";
import { logoImage, companyInfo } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ortiz_admin_token";

function LoginView({ onSuccess }) {
  const { toast } = useToast();
  const [mode, setMode] = useState("password"); // 'password' | 'pin'
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setLoading(true);
    try {
      const body = mode === "password" ? { password: value } : { pin: value };
      const res = await axios.post(`${API}/admin/login`, body);
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onSuccess(res.data.token);
    } catch (err) {
      toast({
        title: "Login failed",
        description: err?.response?.data?.detail || "Invalid credentials",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img
            src={logoImage}
            alt="Ortiz Truck & Landscaping"
            className="h-20 w-auto mb-4"
          />
          <div className="font-display font-bold text-[18px] text-[#f4efe6]">
            {companyInfo.name}
          </div>
          <div className="text-[11px] tracking-[0.22em] text-[#8a877f] font-medium mt-1">
            ADMIN DASHBOARD
          </div>
        </div>

        <form onSubmit={submit} className="card-outline rounded-xl p-7 space-y-5">
          <div className="flex items-center gap-2 justify-center">
            <div className="h-9 w-9 rounded-full chip flex items-center justify-center">
              <Lock size={15} className="text-[#c9b98a]" />
            </div>
            <h1 className="font-display font-bold text-[19px] text-[#f4efe6]">
              Sign In
            </h1>
          </div>

          <div className="grid grid-cols-2 gap-2 chip rounded-lg p-1">
            {["password", "pin"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  setValue("");
                }}
                className={`text-[11.5px] font-semibold tracking-[0.14em] uppercase py-2 rounded-md transition-colors ${
                  mode === m
                    ? "bg-[#f4efe6] text-[#0a0a0b]"
                    : "text-[#a9a69d] hover:text-white"
                }`}
              >
                {m}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-[11.5px] font-semibold uppercase tracking-[0.16em] text-[#a9a69d] mb-2">
              {mode === "password" ? "Password" : "PIN"}
            </label>
            <input
              type={mode === "password" ? "password" : "text"}
              inputMode={mode === "pin" ? "numeric" : "text"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={mode === "password" ? "Enter password" : "Enter PIN"}
              className="field w-full rounded-md px-4 py-3 text-[14px]"
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-md text-[13.5px] font-semibold disabled:opacity-70"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>

          <a
            href="/"
            className="block text-center text-[12px] text-[#8a877f] hover:text-white"
          >
            &larr; Back to Website
          </a>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const { toast } = useToast();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // 'all' | 'new' | 'contacted'
  const authHeader = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/quotes`, {
        headers: authHeader,
      });
      setQuotes(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast({
          title: "Failed to load",
          description: err?.response?.data?.detail || "Try refreshing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleContacted = async (q) => {
    try {
      await axios.patch(
        `${API}/admin/quotes/${q.id}`,
        { contacted: !q.contacted },
        { headers: authHeader }
      );
      setQuotes((prev) =>
        prev.map((x) => (x.id === q.id ? { ...x, contacted: !x.contacted } : x))
      );
    } catch (err) {
      toast({ title: "Update failed", description: "Try again" });
    }
  };

  const removeQuote = async (q) => {
    if (!window.confirm(`Delete quote from ${q.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/quotes/${q.id}`, {
        headers: authHeader,
      });
      setQuotes((prev) => prev.filter((x) => x.id !== q.id));
      toast({ title: "Quote deleted" });
    } catch (err) {
      toast({ title: "Delete failed", description: "Try again" });
    }
  };

  const visible = quotes.filter((q) =>
    filter === "all"
      ? true
      : filter === "new"
      ? !q.contacted
      : q.contacted
  );

  const stats = {
    total: quotes.length,
    newCount: quotes.filter((q) => !q.contacted).length,
    contactedCount: quotes.filter((q) => q.contacted).length,
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-[#f4efe6]">
      {/* Header */}
      <div className="border-b border-white/[0.06] bg-[#0a0a0b]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logoImage}
              alt="Ortiz"
              className="h-12 w-auto"
            />
            <div className="leading-tight">
              <div className="font-display font-bold text-[15.5px]">
                {companyInfo.name}
              </div>
              <div className="text-[10px] tracking-[0.22em] text-[#8a877f] font-medium">
                ADMIN DASHBOARD
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="btn-ghost inline-flex items-center gap-2 px-3 py-2 rounded-md text-[12px] font-semibold"
              title="Refresh"
            >
              <RefreshCw size={13} />
              Refresh
            </button>
            <button
              onClick={onLogout}
              className="btn-ghost inline-flex items-center gap-2 px-3 py-2 rounded-md text-[12px] font-semibold"
            >
              <LogOut size={13} />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <StatCard
            icon={ClipboardList}
            label="Total Quotes"
            value={stats.total}
          />
          <StatCard
            icon={Circle}
            label="New"
            value={stats.newCount}
            accent
          />
          <StatCard
            icon={CheckCircle2}
            label="Contacted"
            value={stats.contactedCount}
          />
        </div>

        {/* Filter tabs */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-bold text-[22px]">
            Quote Requests
          </h2>
          <div className="inline-flex items-center gap-1 chip rounded-lg p-1">
            {[
              { id: "all", label: `All (${stats.total})` },
              { id: "new", label: `New (${stats.newCount})` },
              { id: "contacted", label: `Contacted (${stats.contactedCount})` },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                className={`text-[11.5px] font-semibold tracking-[0.1em] uppercase px-3 py-2 rounded-md transition-colors ${
                  filter === t.id
                    ? "bg-[#f4efe6] text-[#0a0a0b]"
                    : "text-[#a9a69d] hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="card-outline rounded-xl p-10 text-center text-[#8a877f]">
            Loading quotes...
          </div>
        ) : visible.length === 0 ? (
          <div className="card-outline rounded-xl p-14 text-center">
            <Truck size={28} className="text-[#c9b98a] mx-auto mb-3" />
            <div className="font-display font-bold text-[16px]">
              No quotes {filter !== "all" ? `in "${filter}"` : "yet"}
            </div>
            <div className="text-[13px] text-[#8a877f] mt-1.5">
              Requests submitted through the website will appear here.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((q) => (
              <QuoteCard
                key={q.id}
                q={q}
                onToggle={() => toggleContacted(q)}
                onDelete={() => removeQuote(q)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-outline rounded-xl p-5 flex items-center gap-4">
      <div
        className={`h-11 w-11 rounded-lg chip flex items-center justify-center ${
          accent ? "ring-1 ring-[#c9b98a]/40" : ""
        }`}
      >
        <Icon size={17} className="text-[#c9b98a]" />
      </div>
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8a877f] font-semibold">
          {label}
        </div>
        <div className="font-display font-extrabold text-[26px] text-[#f4efe6] leading-none mt-1">
          {value}
        </div>
      </div>
    </div>
  );
}

function QuoteCard({ q, onToggle, onDelete }) {
  const created = new Date(q.createdAt);
  const dateStr = created.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <article className="card-outline rounded-xl p-5 md:p-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-2 mb-2">
            <h3 className="font-display font-bold text-[17px] text-[#f4efe6]">
              {q.name}
            </h3>
            {q.contacted ? (
              <span className="chip rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-[#8fce9c]">
                Contacted
              </span>
            ) : (
              <span className="chip rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-[#c9b98a]">
                New
              </span>
            )}
            <span className="text-[11.5px] text-[#8a877f] ml-1">
              {dateStr}
            </span>
          </div>

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px] text-[#c8c5bc]">
            <a
              href={`tel:${q.phone}`}
              className="flex items-center gap-2 hover:text-white"
            >
              <Phone size={13} className="text-[#c9b98a]" />
              {q.phone}
            </a>
            {q.email && (
              <a
                href={`mailto:${q.email}`}
                className="flex items-center gap-2 hover:text-white truncate"
              >
                <Mail size={13} className="text-[#c9b98a]" />
                <span className="truncate">{q.email}</span>
              </a>
            )}
            {q.city && (
              <div className="flex items-center gap-2">
                <MapPin size={13} className="text-[#c9b98a]" />
                {q.city}
              </div>
            )}
            {q.serviceType && (
              <div className="flex items-center gap-2">
                <Truck size={13} className="text-[#c9b98a]" />
                {q.serviceType}
              </div>
            )}
          </div>

          {(q.materialClass || q.yards) && (
            <div className="mt-3 pt-3 border-t divider-soft flex flex-wrap gap-x-6 gap-y-1 text-[12.5px] text-[#a9a69d]">
              {q.materialClass && (
                <div>
                  <span className="text-[#8a877f]">Material:</span>{" "}
                  <span className="text-[#f4efe6] font-medium">
                    {q.materialClass}
                  </span>
                </div>
              )}
              {q.yards && (
                <div>
                  <span className="text-[#8a877f]">Yards:</span>{" "}
                  <span className="text-[#f4efe6] font-medium">{q.yards}</span>
                </div>
              )}
            </div>
          )}

          {q.description && (
            <div className="mt-3 pt-3 border-t divider-soft">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8a877f] font-semibold mb-1.5">
                Notes
              </div>
              <p className="text-[13px] text-[#c8c5bc] leading-relaxed whitespace-pre-wrap">
                {q.description}
              </p>
            </div>
          )}
        </div>

        <div className="flex md:flex-col gap-2 shrink-0">
          <button
            onClick={onToggle}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-[12px] font-semibold ${
              q.contacted ? "btn-ghost" : "btn-primary"
            }`}
          >
            {q.contacted ? (
              <>
                <Circle size={13} />
                Mark New
              </>
            ) : (
              <>
                <CheckCircle2 size={13} />
                Mark Contacted
              </>
            )}
          </button>
          <button
            onClick={onDelete}
            className="btn-ghost inline-flex items-center gap-2 px-3 py-2 rounded-md text-[12px] font-semibold text-[#e08e8e] hover:text-[#f4a2a2]"
            title="Delete"
          >
            <Trash2 size={13} />
            Delete
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() =>
    localStorage.getItem(TOKEN_KEY)
  );

  const handleLogout = async () => {
    try {
      if (token) {
        await axios.post(
          `${API}/admin/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (_) {}
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginView onSuccess={setToken} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
}
